import { useCallback } from 'react';
import { has, isString } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import Types
import type { AlertTypesKeys } from 'types';
// Import UI Components
import { Toaster } from '@geneui/components';
// Import Components
import ToasterMessage from 'components/common/ToasterMessage';
// Import Constants
import { AlertTypes, l, ErrorMessageSymbolsCount, MaxTimeoutDelay } from 'constants/common';

const { warning, error, success, info, danger } = AlertTypes;

/**
 * Shows geneui toasters with a duration derived from the message length.
 * The toaster stays open while hovered: the library timer is disabled by passing a delay that never fires,
 * and `ToasterMessage` owns the real timer so it can pause on mouse enter and restart on mouse leave.
 */
const useToaster = () => {
    const { t } = useTranslation();

    const showToaster = useCallback(
        (type?: Nullable<AlertTypesKeys>, message?: Nullable<string>, defaultDuration = 5000) => {
            if (!has(AlertTypes, type ?? '')) {
                // eslint-disable-next-line no-console
                console.log('You should choose Toaster type from AlertTypes constants.');
                return;
            }

            if (!isString(message)) {
                // eslint-disable-next-line no-console
                console.log('message argument should be type of string.');
                return;
            }

            const calculatedDuration = (Math.ceil(message.length / ErrorMessageSymbolsCount) - 1) * 1000;

            const duration = calculatedDuration > defaultDuration ? calculatedDuration : defaultDuration;

            const toasterOptions = {
                message: <ToasterMessage message={message} duration={duration} />,
                duration: MaxTimeoutDelay,
            };

            switch (type) {
                case success:
                    Toaster.success({
                        ...toasterOptions,
                        iconProps: { type: 'bc-icon-success-fill' },
                        title: t(l.Success),
                    });
                    break;
                case warning:
                    Toaster.warning({
                        ...toasterOptions,
                        iconProps: { type: 'bc-icon-info-fill' },
                        title: t(l.Warning),
                    });
                    break;
                case danger:
                case error:
                    Toaster.error({
                        ...toasterOptions,
                        iconProps: { type: 'bc-icon-error-fill' },
                        title: t(l.Error),
                    });
                    break;
                case info:
                    Toaster.info({
                        ...toasterOptions,
                        iconProps: { type: 'bc-icon-info-fill' },
                        title: t(l.Info),
                    });
                    break;
                default:
                    break;
            }
        },
        [t],
    );

    return { showToaster };
};

export default useToaster;
